import * as z from "@-/com.npmjs.zod";

/**
 * PRC 通用规范汉字表 bands a simplified-script user can include in their browsing corpus. `unlisted`
 * covers every glyph absent from the table (component-only glyphs, traditional forms, variants).
 */
export const SIMPLIFIED_CHARACTER_TIER_OPTIONS = ["tier1", "tier2", "tier3", "unlisted"] as const;
export type SimplifiedCharacterTier = (typeof SIMPLIFIED_CHARACTER_TIER_OPTIONS)[number];

/**
 * Taiwan MOE chart bands a traditional-script user can include in their browsing corpus: 常用字
 * (`common`), 次常用字 (`less-than-common`), 罕用字 (`rarely-used`), and `unlisted` for every glyph
 * absent from all three charts.
 */
export const TRADITIONAL_CHARACTER_USAGE_OPTIONS = [
  "common",
  "less-than-common",
  "rarely-used",
  "unlisted",
] as const;
export type TraditionalCharacterUsage = (typeof TRADITIONAL_CHARACTER_USAGE_OPTIONS)[number];

/** Frequency-rank cutoffs offered for characters (SUBTLEX-CH-CHR ≈ 5.9k rows, BIAU ≈ 5.7k rows). */
export const CHARACTER_MAX_RANK_OPTIONS = [1000, 2000, 3000, 4000, 5000] as const;
/** Frequency-rank cutoffs offered for words (SUBTLEX-CH-WF / BIAU word lists). */
export const WORD_MAX_RANK_OPTIONS = [5000, 10_000, 20_000, 30_000, 50_000] as const;

const maxRankSchema = z.int().positive().nullable();

function uniqueArray<const Options extends readonly [string, ...string[]]>(options: Options) {
  return z
    .array(z.enum(options))
    .max(options.length)
    .refine((values) => values.every((value, index) => values.indexOf(value) === index))
    .readonly();
}

export const simplifiedCorpusSettingsSchema = z.object({
  /** Hide characters whose SUBTLEX-CH-CHR rank is worse than this (or that have no rank); `null` = no cutoff. */
  characterMaxRank: maxRankSchema,
  characterTiers: uniqueArray(SIMPLIFIED_CHARACTER_TIER_OPTIONS),
  /** Hide words whose SUBTLEX-CH-WF rank is worse than this (or that have no rank); `null` = no cutoff. */
  wordMaxRank: maxRankSchema,
});
export type SimplifiedCorpusSettings = z.infer<typeof simplifiedCorpusSettingsSchema>;

export const traditionalCorpusSettingsSchema = z.object({
  /** Hide characters whose BIAU character rank is worse than this (or that have no rank); `null` = no cutoff. */
  characterMaxRank: maxRankSchema,
  characterUsages: uniqueArray(TRADITIONAL_CHARACTER_USAGE_OPTIONS),
  /** Hide words whose BIAU word rank is worse than this (or that have no rank); `null` = no cutoff. */
  wordMaxRank: maxRankSchema,
});
export type TraditionalCorpusSettings = z.infer<typeof traditionalCorpusSettingsSchema>;

/**
 * Which characters and words the non-curated parts of the app (search, browse tabs, compounds,
 * similar characters, words-containing lists, …) surface. Kept per script because the source
 * lists differ: simplified uses PRC tiers + SUBTLEX-CH, traditional uses the Taiwan MOE charts +
 * BIAU. Character and word filters are independent — hiding a character never hides the words
 * written with it. The curated curriculum ignores these settings, and a hidden morpheme's detail
 * page stays reachable by direct navigation (e.g. tapping a character inside a word).
 */
export const corpusSettingsSchema = z.object({
  simplified: simplifiedCorpusSettingsSchema,
  traditional: traditionalCorpusSettingsSchema,
});
export type CorpusSettings = z.infer<typeof corpusSettingsSchema>;

export const DEFAULT_CORPUS_SETTINGS = {
  simplified: { characterMaxRank: null, characterTiers: ["tier1"], wordMaxRank: null },
  traditional: { characterMaxRank: null, characterUsages: ["common"], wordMaxRank: null },
} as const satisfies CorpusSettings;

/**
 * Old caches, rows synced from newer app versions and invalid imports resolve per script to the
 * defaults, so an unreadable value can never blank every browsing surface.
 */
export function resolveCorpusSettings(value: unknown): CorpusSettings {
  const record = z
    .looseObject({ simplified: z.unknown(), traditional: z.unknown() })
    .safeParse(value);
  const simplified = simplifiedCorpusSettingsSchema.safeParse(
    record.success ? record.data.simplified : undefined,
  );
  const traditional = traditionalCorpusSettingsSchema.safeParse(
    record.success ? record.data.traditional : undefined,
  );
  return {
    simplified: simplified.success ? simplified.data : DEFAULT_CORPUS_SETTINGS.simplified,
    traditional: traditional.success ? traditional.data : DEFAULT_CORPUS_SETTINGS.traditional,
  };
}
